/**
 * Record or revise one bidder's durable quote through the RFQ operating API.
 *
 * A revision is a full restated offer (append-only backend contract) — the
 * dialog is therefore the same for create and revise, prefilled from the
 * current revision when revising. Only fields the operator states are sent;
 * an unstated qualification stays null, which the backend treats as "the
 * quote does not say" — never as confirmed.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { rfqApi, classifyWorkflowError } from "../../services/intelligenceService";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Loader2 } from "lucide-react";

const EMPTY_FORM = {
  bidderDisplayName: "",
  vendorId: "",
  baseAmount: "",
  proposedDurationDays: "",
  leadTimeDays: "",
  validUntil: "",
  bondingConfirmed: "unstated",
  insuranceConfirmed: "unstated",
  insuranceExpiresOn: "",
  inclusions: "",
  exclusions: "",
  qualificationsText: "",
  sourceNote: "",
  submit: true,
  revisionReason: "",
};

const linesToExceptions = (text) =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((description) => ({ description }));

const triState = (value) => (value === "yes" ? true : value === "no" ? false : null);

const QuoteRecorder = ({ open, onOpenChange, packageId, vendors = [], reviseQuote, onSaved }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const isRevision = Boolean(reviseQuote);

  useEffect(() => {
    if (!open) return;
    if (reviseQuote) {
      setForm({
        ...EMPTY_FORM,
        bidderDisplayName: reviseQuote.bidderDisplayName || "",
        vendorId: reviseQuote.vendorId || "",
        baseAmount: reviseQuote.baseAmount ?? "",
        proposedDurationDays: reviseQuote.proposedDurationDays ?? "",
        leadTimeDays: reviseQuote.leadTimeDays ?? "",
        validUntil: reviseQuote.validUntil || "",
        bondingConfirmed:
          reviseQuote.bondingConfirmed === true ? "yes" : reviseQuote.bondingConfirmed === false ? "no" : "unstated",
        insuranceConfirmed:
          reviseQuote.insuranceConfirmed === true ? "yes" : reviseQuote.insuranceConfirmed === false ? "no" : "unstated",
        insuranceExpiresOn: reviseQuote.insuranceExpiresOn || "",
        inclusions: (reviseQuote.inclusions || []).map((i) => i.description || "").join("\n"),
        exclusions: (reviseQuote.exclusions || []).map((i) => i.description || "").join("\n"),
        qualificationsText: (reviseQuote.qualifications || []).map((i) => i.description || "").join("\n"),
        sourceNote: reviseQuote.sourceNote || "",
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, reviseQuote]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    if (!isRevision && !form.bidderDisplayName.trim()) {
      toast.error("The bidder's name is required.");
      return;
    }
    const body = {
      baseAmount: form.baseAmount === "" ? null : Number(form.baseAmount),
      proposedDurationDays: form.proposedDurationDays === "" ? null : Number(form.proposedDurationDays),
      leadTimeDays: form.leadTimeDays === "" ? null : Number(form.leadTimeDays),
      validUntil: form.validUntil || null,
      bondingConfirmed: triState(form.bondingConfirmed),
      insuranceConfirmed: triState(form.insuranceConfirmed),
      insuranceExpiresOn: form.insuranceExpiresOn || null,
      inclusions: linesToExceptions(form.inclusions),
      exclusions: linesToExceptions(form.exclusions),
      qualifications: linesToExceptions(form.qualificationsText),
      sourceNote: form.sourceNote || null,
      submit: Boolean(form.submit),
    };
    setSaving(true);
    try {
      if (isRevision) {
        await rfqApi.reviseQuote(reviseQuote.quoteId, {
          ...body,
          revisionReason: form.revisionReason || null,
        });
        toast.success("Revision recorded — the prior revision stays on record.");
      } else {
        await rfqApi.createQuote(packageId, {
          ...body,
          bidderDisplayName: form.bidderDisplayName.trim(),
          vendorId: form.vendorId || null,
        });
        toast.success(form.submit ? "Quote recorded as submitted." : "Draft quote recorded.");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(classifyWorkflowError(err).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isRevision ? `Revise quote — ${reviseQuote?.bidderDisplayName}` : "Record a quote"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {!isRevision && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Bidder name *</Label>
                <Input className="mt-1" value={form.bidderDisplayName} onChange={(e) => set("bidderDisplayName")(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Vendor (canonical identity)</Label>
                <Select value={form.vendorId || "none"} onValueChange={(v) => set("vendorId")(v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unlinked (name only)</SelectItem>
                    {vendors.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {isRevision && (
            <div>
              <Label className="text-xs">Revision reason</Label>
              <Input className="mt-1" value={form.revisionReason} onChange={(e) => set("revisionReason")(e.target.value)} placeholder="What changed and why" />
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Base amount (USD)</Label>
              <Input type="number" min="0" className="mt-1" value={form.baseAmount} onChange={(e) => set("baseAmount")(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Duration (days)</Label>
              <Input type="number" min="0" className="mt-1" value={form.proposedDurationDays} onChange={(e) => set("proposedDurationDays")(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Lead time (days)</Label>
              <Input type="number" min="0" className="mt-1" value={form.leadTimeDays} onChange={(e) => set("leadTimeDays")(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Valid until</Label>
              <Input type="date" className="mt-1" value={form.validUntil} onChange={(e) => set("validUntil")(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Bonding confirmed</Label>
              <Select value={form.bondingConfirmed} onValueChange={set("bondingConfirmed")}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unstated">Quote doesn't say</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Insurance confirmed</Label>
              <Select value={form.insuranceConfirmed} onValueChange={set("insuranceConfirmed")}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unstated">Quote doesn't say</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.insuranceConfirmed === "yes" && (
            <div>
              <Label className="text-xs">Insurance expires on</Label>
              <Input type="date" className="mt-1 w-48" value={form.insuranceExpiresOn} onChange={(e) => set("insuranceExpiresOn")(e.target.value)} />
            </div>
          )}
          <div>
            <Label className="text-xs">Inclusions (one per line)</Label>
            <Textarea className="mt-1" rows={2} value={form.inclusions} onChange={(e) => set("inclusions")(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Exclusions (one per line — these drive responsiveness)</Label>
            <Textarea className="mt-1" rows={2} value={form.exclusions} onChange={(e) => set("exclusions")(e.target.value)} placeholder={"e.g.\nFactory and field testing by others\nStartup by others"} />
          </div>
          <div>
            <Label className="text-xs">Qualifications (one per line)</Label>
            <Textarea className="mt-1" rows={2} value={form.qualificationsText} onChange={(e) => set("qualificationsText")(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Source note (where this quote came from)</Label>
            <Input className="mt-1" value={form.sourceNote} onChange={(e) => set("sourceNote")(e.target.value)} placeholder="e.g. Emailed PDF from bidder, 2026-08-12" />
          </div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
            <input type="checkbox" checked={form.submit} onChange={(e) => set("submit")(e.target.checked)} />
            Record as submitted (unchecked saves a draft the operator is still transcribing)
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={save} disabled={saving}>
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
              {isRevision ? "Record revision" : "Record quote"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteRecorder;
